import { ABSOLUTE_X_AXIS, CUT_WALL_THICKNESS } from '@constants'
import { toVector3 } from '@utils/conversion/toVector3'
import findIntersection from '@utils/generalMaths/findIntersection'
import getPrimeCoordinates from './getPrimeCoordinates'
type Props<T extends [number, number] = [number, number], K extends number = number> = {
  AStart: T
  AEnd: T
  BStart: T
  BEnd: T
  AAngle: K
  BAngle: K
  AThickness: K
  BThickness: K
}

/**
 * This function returns the relevant points for two any lines that share (or not) the end/start
 * ASSUMPTIONS:
 * - lineA is before lineB
 * REMEMBER:
 * - This function does NOT duped values for B line. Remember:
 * B.prevDot = dotAB, B.prevCross = cross
 */

export default function getRelevantPoints({
  AAngle,
  AEnd,
  AStart,
  BEnd,
  AThickness,
  BAngle,
  BStart,
  BThickness,
}: Props) {
  const Sa = toVector3(AStart)
  const Ea = toVector3(AEnd)
  const Sb = toVector3(BStart)
  const Eb = toVector3(BEnd)
  //These vectors should be facing to the opposite sides. So we can calc the angle between them.
  /**
   * ----------Ea===Sb
   *              *
   *  *-------angle
   * Sa
   * -------------------------*
   *--------------------------Eb
   */
  //In order to find the point P' which is the crossing of E'a and S'b, we need to calculate those first:
  const { EPrimeA, EPrimeB, SPrimeA, SPrimeB } = getPrimeCoordinates({
    Ea,
    Sa,
    Sb,
    Eb,
    AThickness,
    BThickness,
    AAngle,
    BAngle,
  })

  const mergePoint = findIntersection(SPrimeA, EPrimeA, EPrimeB, SPrimeB) // El vértice a la derecha
  const directionA = Ea.clone().sub(Sa).normalize() // + 0 +

  const directionB = Eb.clone().sub(Sb).normalize() // - 0 -
  const crossAB = directionA.clone().cross(directionB)
  const dotAB = directionA.clone().dot(directionB)

  const betaVector = mergePoint.clone().sub(Ea).normalize()
  const beta = betaVector.clone().normalize().angleTo(ABSOLUTE_X_AXIS)
  const absoluteCross = betaVector.clone().cross(ABSOLUTE_X_AXIS)
  const absoluteDot = betaVector.clone().dot(ABSOLUTE_X_AXIS)

  const normalMergePointVector = mergePoint.clone().sub(Ea).normalize() // VectorV

  // TODO: crear un punto que pertenezca al vector normalMervePointVector y pase a 300 unidades del end

  const rightHandPerpendicularMergePoint = toVector3([
    -normalMergePointVector.z,
    normalMergePointVector.x,
  ])
  const leftHandPerpendicularMergePoint = toVector3([
    normalMergePointVector.z,
    -normalMergePointVector.x,
  ])

  const rightPerpendicularPoint = mergePoint
    .clone()
    .add(rightHandPerpendicularMergePoint.clone().multiplyScalar(CUT_WALL_THICKNESS))

  const leftPerpendicularPoint = mergePoint
    .clone()
    .add(leftHandPerpendicularMergePoint.clone().multiplyScalar(CUT_WALL_THICKNESS))

  const cross = Math.sign(crossAB.y)

  return {
    beta,
    dotAB,
    cross,
    absoluteCross: absoluteCross.y,
    absoluteDot,
    symmetricalPerpendicularPoint: leftPerpendicularPoint,
    perpendicularPoint: rightPerpendicularPoint,
  }
}
